import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";
import AppContainer from "../Structure/AppContainer";
import Loader from "../Common/Loader";
import ajaxStations from "../../util/remote/ajaxStations";
import ajaxFuelCategories from "../../util/remote/ajaxFuelCategories";
import ajaxFuelPumps from "../../util/remote/ajaxFuelPumps";
import ajaxShiftTemplate from "../../util/remote/ajaxShiftTemplate";
import ajaxUser from "../../util/remote/ajaxUser";
import ajaxShift from "../../util/remote/ajaxShift";
import AuthContext from "../../Context/AuthContext";
const StartEmployeeShift = () => {
  const { userId } = useContext(AuthContext);
  const station_id = Number(window.localStorage.getItem("FuelFy@station_id"));
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [stations, setStations] = useState([]);
  // Pumps, Shifts, Fuels
  const [pumps, setPumps] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [fuels, setFuels] = useState([]);
  // Form fields
  const [pumpID, setPumpID] = useState(null);
  const [shiftID, setShiftID] = useState(null);
  const [shiftDate, setShiftDate] = useState(today);
  const [checkinTime, setCheckinTime] = useState(""); // Manual check-in time
  const [fuelReadings, setFuelReadings] = useState([]); // [{ fuel_id, meter_reading }]
  const [employee, setEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  // Fetch stations
  useEffect(() => {
    const fetchStations = async () => {
      const res = await ajaxStations.liststations_mini_data();
      if (res.status === "OK") {
        setStations(
          res.details.map((st) => ({
            value: Number(st.id),
            label: st.station_name,
          }))
        );
      } else toast.error(res.message);
    };
    fetchStations();
  }, []);
  // Fetch shift templates
  const fetchShifts = async () => {
    if (!station_id) return;
    setLoading(true);
    const res = await ajaxShiftTemplate.list_shit_templates({
      station_id: station_id,
    });
    if (res.status === "OK") {
      setShifts(
        res.details.map((sh) => ({ value: sh.id, label: sh.shift_name }))
      );
    } else toast.error(res.message);
    setLoading(false);
  };
  useEffect(() => {
    fetchShifts();
  }, [station_id]);
  // Fetch pumps
  const fetchPumps = async () => {
    if (!station_id) return;
    setLoading(true);
    const res = await ajaxFuelPumps.list_Pumps({ station_id: station_id });
    if (res.status === "OK") {
      setPumps(
        res.details.map((pm) => ({ value: pm.id, label: pm.pump_code }))
      );
    } else toast.error(res.message);
    setLoading(false);
  };
  useEffect(() => {
    fetchPumps();
  }, [station_id]);
  // Fetch fuel categories
  const fetchFuelCategories = async () => {
    if (!station_id) return;
    const res = await ajaxFuelCategories.list_fuel_categories({
      station_id: station_id,
    });
    if (res.status === "OK") {
      setFuels(
        res.details.map((d) => ({ value: d.id, label: d.fuel_category }))
      );
    } else toast.error(res.message);
  };
  useEffect(() => {
    fetchFuelCategories();
  }, [station_id]);
  // Fetch employees
  const getEmployees = async () => {
    const res = await ajaxUser.getUsersMinimal_data({ station_id: station_id });
    if (res.status === "OK") setEmployees(res.details);
    else setEmployees([]);
  };
  useEffect(() => {
    getEmployees();
  }, [station_id]);
  // Handle pump change -> initialize fuel readings
  const handlePumpChange = (selectedPump) => {
    setPumpID(selectedPump.value);
    const initialReadings = fuels.map((f) => ({
      fuel_id: f.value,
      meter_reading: "",
    }));
    setFuelReadings(initialReadings);
  };
  // Step navigation
  const nextStep = () => {
    if (step === 1) {
      if (!station_id || !pumpID || !shiftID || !employee)
        return toast.error("Please select station, pump, shift, and employee.");
    }
    if (step === 2) {
      if (fuelReadings.some((fr) => !fr.meter_reading))
        return toast.error(
          "Please enter opening meter readings for all fuels."
        );
    }
    setStep(step + 1);
  };
  const prevStep = () => setStep(step - 1);
  // Submit check-in
  const handleSubmit = async () => {
    const payload = {
      station_id: station_id,
      shift_id: shiftID,
      employee_id: employee,
      created_by: userId,
      checkin_time: checkinTime || new Date().toISOString(), // Use manual time or auto
      date: shiftDate,
      fuel_readings: fuelReadings.map((fr) => ({
        fuel_id: fr.fuel_id,
        pump_id: pumpID,
        opening_meter_reading: fr.meter_reading,
      })),
    };
    setLoading(true);
    const res = await ajaxShift.open_shift(payload);
    setLoading(false);
    if (res.status === "OK") {
      toast.success(res.message || "Check-in recorded successfully!");
      setTimeout(() => navigate("/attendence/active_sessions"), 1200);
    } else {
      toast.error(res.message);
    }
  };
  const filteredActions = [
    {
      label: "View Current Sessions",
      icon: "fa-solid fa-file-pen",
      className: "btn btn-primary btn-sm",
      onClick: () => navigate("/attendence/active_sessions"),
    },
    {
      label: "Register Price",
      icon: "fa-solid fa-file-pen",
      className: "btn btn-info btn-sm",
    },
  ];
  return (
    <AppContainer
      title="Shift Opening Form"
      previousPageTitle="Dashboard"
      actions={filteredActions}
    >
      <div className="container-fluid">
        <Toaster />
        {loading && <Loader />}
        <div className="row row-sm">
          <div className="col-lg-12">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">Open Shift</h6>
                {/* Wizard Steps */}
                <div className="d-flex justify-content-between mb-4">
                  {["Shift Info", "Meters", "Review"].map((label, index) => {
                    const stepNum = index + 1;
                    const isActive = step === stepNum;
                    const isCompleted = step > stepNum;
                    return (
                      <div
                        key={stepNum}
                        className="text-center flex-fill position-relative"
                      >
                        <div
                          className={`rounded-circle mx-auto mb-1 ${
                            isActive
                              ? "bg-primary text-white"
                              : isCompleted
                              ? "bg-success text-white"
                              : "bg-light text-muted"
                          }`}
                          style={{
                            width: "32px",
                            height: "32px",
                            lineHeight: "32px",
                          }}
                        >
                          {stepNum}
                        </div>
                        <small
                          className={
                            isActive || isCompleted ? "text-dark" : "text-muted"
                          }
                        >
                          {label}
                        </small>
                      </div>
                    );
                  })}
                </div>
                {/* STEP 1 */}
                {step === 1 && (
                  <section>
                    <div className="form-group mb-3">
                      <label>Station *</label>
                      <Select
                        isDisabled
                        options={stations}
                        value={stations.find((x) => x.value === station_id)}
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label>Employee *</label>
                      <Select
                        onChange={(e) => setEmployee(e.id)}
                        getOptionLabel={(opt) => opt.User_name}
                        getOptionValue={(opt) => opt.id}
                        options={employees}
                        value={employees.find((e) => e.id === employee) || null}
                        isSearchable
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label>Pump *</label>
                      <Select
                        options={pumps}
                        value={pumps.find((x) => x.value === pumpID)}
                        onChange={handlePumpChange}
                        placeholder="Select pump"
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label>Shift *</label>
                      <Select
                        options={shifts}
                        value={shifts.find((x) => x.value === shiftID)}
                        onChange={(e) => setShiftID(e.value)}
                        placeholder="Select shift"
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label>Shift Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={shiftDate}
                        onChange={(e) => setShiftDate(e.target.value)}
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label>Check-In Time (Optional)</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={checkinTime}
                        onChange={(e) => setCheckinTime(e.target.value)}
                        placeholder="Select check-in time"
                      />
                      <small className="text-muted">
                        Leave empty to use current time automatically
                      </small>
                    </div>
                  </section>
                )}
                {/* STEP 2 */}
                {step === 2 && (
                  <section>
                    {fuelReadings.map((fr, idx) => {
                      const fuelLabel = fuels.find(
                        (f) => f.value === fr.fuel_id
                      )?.label;
                      return (
                        <div className="form-group mb-3" key={fr.fuel_id}>
                          <label>Opening Meter for {fuelLabel} *</label>
                          <input
                            type="number"
                            className="form-control"
                            value={fr.meter_reading}
                            onChange={(e) => {
                              const updated = [...fuelReadings];
                              updated[idx].meter_reading = e.target.value;
                              setFuelReadings(updated);
                            }}
                            placeholder={`Enter meter reading for ${fuelLabel}`}
                          />
                        </div>
                      );
                    })}
                  </section>
                )}
                {/* STEP 3 - Review */}
                {step === 3 && (
                  <section>
                    <h6>Review Information</h6>
                    <ul className="list-group mb-3">
                      <li className="list-group-item">
                        <strong>Station: </strong>
                        {stations.find((x) => x.value === station_id)?.label}
                      </li>
                      <li className="list-group-item">
                        <strong>Employee: </strong>
                        {employees.find((e) => e.id === employee)?.User_name}
                      </li>
                      <li className="list-group-item">
                        <strong>Pump: </strong>
                        {pumps.find((x) => x.value === pumpID)?.label}
                      </li>
                      <li className="list-group-item">
                        <strong>Shift: </strong>
                        {shifts.find((x) => x.value === shiftID)?.label}
                      </li>
                      <li className="list-group-item">
                        <strong>Shift Date: </strong> {shiftDate}
                      </li>
                      <li className="list-group-item">
                        <strong>Check-In Time: </strong>
                        {checkinTime || new Date().toLocaleString()}
                      </li>
                      {fuelReadings.map((fr) => (
                        <li key={fr.fuel_id} className="list-group-item">
                          <strong>
                            {fuels.find((f) => f.value === fr.fuel_id)?.label}
                            Meter:
                          </strong>
                          {fr.meter_reading}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  {step > 1 && (
                    <button className="btn btn-secondary" onClick={prevStep}>
                      Back
                    </button>
                  )}
                  {step < 3 && (
                    <button
                      className="btn btn-primary ms-auto"
                      onClick={nextStep}
                    >
                      Next
                    </button>
                  )}
                  {step === 3 && (
                    <button
                      className="btn btn-success ms-auto"
                      onClick={handleSubmit}
                    >
                      Submit Check-In
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppContainer>
  );
};
export default StartEmployeeShift;
